import { action } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import {
  didCancel,
  type Task,
  task,
  type TaskInstance,
} from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type HotelSettingsPmsIntegrationController from 'lumen/controllers/hotel/settings/pms-integration';
import type { Capmsprefilloption } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

interface PmsIntegrationModel {
  launchPmsSettingsSimulateRequestTask: Task<
    { arePmsSettingsValid: boolean | null; isSuccess: boolean },
    [unknown]
  >;
  pmsPrefillOptionsTask: Task<Capmsprefilloption | null | undefined, []>;
  ratePreferenceTaskResult: TaskInstance<void>;
  saveAndUpdatePmsSettingsTask: Task<{ isSuccess: boolean }, [unknown]>;
}

type TransitionWithIsAborted = Transition & { isAborted: boolean };

export default class HotelSettingsPmsIntegrationRoute extends Route {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];

  @service declare router: Services['router'];
  @service declare store: Services['store'];

  leaveWithoutSaving = false;

  confirmLeaveTask = task(
    { drop: true },
    async (transition: TransitionWithIsAborted) => {
      this.leaveWithoutSaving = await this.alerting.showWarningAlert({
        title: this.intl.t('pricing_assistant.warning_banner.header'),
        dangerMode: true,
        buttons: {
          cancel: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.secondary_button.label',
            ),
          },
          confirm: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.primary_button.label',
            ),
          },
        },
      });

      if (this.leaveWithoutSaving) {
        transition.retry();
      }
    },
  );
  launchPmsSettingsSimulateRequestTask = task(
    { restartable: true },
    async (pmsSettings) => {
      let apiResponse = null;

      try {
        const standardParams = {
          hotel_id: this.hotelAndMarketService.hotel.id,
        };

        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/recommendations/simulate/pms',
          // @ts-expect-error: Argument of type '{ hotel_id: string | null; }' is not assignable to parameter of type 'FormatUrlParams'.
          standardParams,
          this.ajax.host,
        );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        apiResponse = await this.ajax.post(requestUrl, {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          data: pmsSettings,
          contentType: 'application/json',
        });

        return {
          isSuccess: true,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          arePmsSettingsValid: apiResponse.carecommendations?.length !== 0,
        };
      } catch (error) {
        // Didn't go for basic error handling for now (one less modal in the users face if the request would fail once)
        if (this.access.isStaffUser && console) {
          console.error(error);
        }

        if (error) {
          this.errorTracking.captureException(error);
        }

        return {
          isSuccess: false,
          arePmsSettingsValid: null,
        };
      }
    },
  );

  pmsPrefillOptionsTask = task({ restartable: true }, async () => {
    let prefillOptions = null;

    try {
      prefillOptions = await this.store.query<Capmsprefilloption>(
        'capmsprefilloption',
        {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/pms/settings/prefills',
          subscription_id: this.hotelAndMarketService.hotel.id,
        },
      );

      return prefillOptions[0];
    } catch (error) {
      this.basicErrorHandling(error);
    }

    return null; // in this case something went wrong with the request
  });

  saveAndUpdatePmsSettingsTask = task({ drop: true }, async (pmsSettings) => {
    try {
      const standardParams = {
        subscription_id: this.hotelAndMarketService.hotel.id,
      };

      const requestUrl = formatUrl(
        '/apigateway/v1/app/pricing-assistant/pms/settings',
        // @ts-expect-error: Argument of type '{ subscription_id: string | null; }' is not assignable to parameter of type 'FormatUrlParams'.
        standardParams,
        this.ajax.host,
      );

      await this.ajax.put(requestUrl, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data: pmsSettings,
        contentType: 'application/json',
      });

      // Saving PMS settings rewrites both the PMS settings themselves and the
      // paRatePreference server-side, so both have to be refreshed for the PA
      // calendar selections to stay correct.
      await Promise.all([
        this.store.query<Hotel>('hotel', {
          id: this.hotelAndMarketService.hotel.id,
        }),
        this.refreshPaRatePreference(),
      ]);

      return {
        isSuccess: true,
      };
    } catch (error) {
      this.basicErrorHandling(error);

      return {
        isSuccess: false,
      };
    }
  });

  activate(transition: Transition): void {
    super.activate(transition);

    // Reset the "leave without saving" state on route activation so the willTransition hook will recheck the unsaved changes
    this.leaveWithoutSaving = false;
  }

  basicErrorHandling(error: unknown): void {
    if (this.access.isStaffUser && console) {
      console.error(error);
    }

    if (error) {
      this.errorTracking.captureException(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting.showErrorAlert({
      text: getAPIErrorMessage(error, this.intl),
    });
  }

  beforeModel(transition: Transition): void {
    if (this.navigation.redirectToAccountSelection(transition)) {
      return;
    }

    if (!this.access.hasPricingAssistantSettingsPmsIntegrationTab) {
      this.router.transitionTo('hotel.settings.index');
    }
  }

  model(): PmsIntegrationModel | Record<string, never> {
    if (this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated) {
      // The controller reads the preferred channel at render, so the template gates
      // on this task. Only requested for an integrated PMS, the only case that reads it.
      const ratePreferenceTaskResult =
        this.pricingAssistantDataRequestService.loadPaRatePreferenceTask.perform(
          this.hotelAndMarketService.hotel.id!,
        );

      const model: PmsIntegrationModel = {
        pmsPrefillOptionsTask: this.pmsPrefillOptionsTask,
        ratePreferenceTaskResult,
        saveAndUpdatePmsSettingsTask: this.saveAndUpdatePmsSettingsTask,
        launchPmsSettingsSimulateRequestTask:
          this.launchPmsSettingsSimulateRequestTask,
      };

      void this.pmsPrefillOptionsTask.perform();

      return model;
    }

    return {};
  }

  async refreshPaRatePreference(): Promise<void> {
    try {
      await this.pricingAssistantDataRequestService.requestPaRatePreferenceTask.perform(
        this.hotelAndMarketService.hotel.id!,
      );
    } catch (e) {
      // The save itself succeeded and reports that, so neither a failed
      // request nor the cancellation from leaving the page mid-save (the task
      // is restartable) may surface as a save failure.
      if (!didCancel(e)) {
        this.errorTracking.addBreadcrumb({
          message:
            'Pricing Assistant rate preference refresh after a PMS settings save failed',
        });
        this.errorTracking.captureException(e);
      }
    }
  }

  resetController(
    controller: HotelSettingsPmsIntegrationController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      // Cancel all possibly running tasks. This will make sure the request is not done anymore if it was waiting
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.pmsPrefillOptionsTask.cancelAll();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.launchPmsSettingsSimulateRequestTask.cancelAll();

      this.store.unloadAll('capmsprefilloption');
      // Unload seasonal strategies so they don't bleed across subscription changes.
      this.store.unloadAll('recommendation-strategy');

      // Reset some controller values when leaving the route
      controller.showPmsSettingsValueInvalidAfterSimulationBanner = false;
    }
  }

  setupController(
    controller: HotelSettingsPmsIntegrationController,
    model: unknown,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    // Make sure to reset the localSettingsCopy on the controller
    // It's an attribute on the controller and otherwise wouldn't be updated when switching hotels (since controller would keep on existing)
    // This means that everytime we enter this route, the localSettingsCopy should have the correct values
    // Note: Since this is executed everytime when entering the route, we don't reset this in the resetController when leaving the route
    controller.resetLocalSettingsCopy();
  }

  @action
  willTransition(transition: TransitionWithIsAborted): void {
    /* eslint-disable ember/no-controller-access-in-routes */
    const hasChanges = (
      this.controllerFor(
        'hotel.settings.pms-integration',
      ) as HotelSettingsPmsIntegrationController
    ).hasUserChangedSettingsValue;
    /* eslint-enable ember/no-controller-access-in-routes */

    if (!hasChanges || transition.isAborted || this.leaveWithoutSaving) {
      return;
    }

    transition.abort();
    void this.confirmLeaveTask.perform(transition);
  }
}
